import React, { useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import LinearProgress from '@material-ui/core/LinearProgress';
import { StatusManager } from 'common/components/StatusManager';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { DRAWER_KEYS, ENDPOINTS } from 'features/saved-searches/constants';
import { getMatchedStaticDateRange } from 'features/saved-searches/utils/savedSearches.utils';
import { useShowAlert } from 'hooks';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import { useUserContext } from 'providers/UserProvider';
import { SECONDARY_API_CLIENT } from 'services/API';
import strings from 'strings';
import { TextBox } from 'UI/components/atoms';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { UIStatus } from 'UI/constants/status';

const COPIES = strings.shared.savedSearches.form;

export const CreationSavedSearchDrawer = ({
  activeSearch,
  entityType,
  onApplySearch,
  onClose,
  onSaveCompleted,
  open = true,
  savedSearchId = null,
  search
}) => {
  const isExistingSearch = !!savedSearchId;
  const [currentUser] = useUserContext();
  const { state } = useFetchWithStatusV2({
    apiInstance: SECONDARY_API_CLIENT,
    endpoint: isExistingSearch ? `${ENDPOINTS.savedSearch.replace(':id', savedSearchId)}` : null,
    initialStatus: UIStatus.Default
  });
  const savedSearch = state?.results;

  const [uiState, setUiState] = useState({
    isSaving: false
  });
  const [formValues, setFormValues] = useState({
    name: '',
    description: ''
  });
  const { showAlert } = useShowAlert();
  const canSave = Boolean(formValues.name?.trim());

  useEffect(() => {
    if (savedSearch && isExistingSearch) {
      setFormValues({
        name: savedSearch.name || '',
        description: savedSearch.description || ''
      });
    }
  }, [isExistingSearch, savedSearch]);

  const applySearch = newSearch => {
    if (!onApplySearch) return;

    const shouldApplySearch = !savedSearchId || savedSearchId === activeSearch?.id;
    shouldApplySearch &&
      onApplySearch({
        search: newSearch,
        drawerToClose: isExistingSearch ? DRAWER_KEYS.edit : DRAWER_KEYS.creation
      });
  };

  const handleClickSave = async () => {
    if (!canSave) return;

    try {
      const matchedStaticDateRange = getMatchedStaticDateRange(search);

      setUiState(prev => ({ ...prev, isSaving: true }));
      const finalSearch = {
        ...(search || savedSearch?.search || {}),
        staticDateRangeId: matchedStaticDateRange?.id || null
      };

      const body = {
        description: formValues.description,
        entityType,
        name: formValues.name,
        search: finalSearch
      };
      const response = !isExistingSearch
        ? await SECONDARY_API_CLIENT.post(ENDPOINTS.savedSearches, body)
        : await SECONDARY_API_CLIENT.patch(`${ENDPOINTS.savedSearches}/${savedSearchId}`, body);

      if (response.status === HTTPStatusCodes.Ok) {
        showAlert({
          title: strings.formatString(COPIES.alerts.success, { action: 'saved' }),
          autoHideDuration: 5000
        });
        applySearch({
          id: savedSearchId || response.data.id,
          ownerId: currentUser?.id,
          ownerName: currentUser?.personalInformation?.full_name,
          ...body
        });
        onSaveCompleted && onSaveCompleted();
      }
    } catch (error) {
      showAlert({
        title: strings.formatString(COPIES.alerts.error, { action: 'saving' }),
        error
      });
    } finally {
      setUiState(prev => ({ ...prev, isSaving: false }));
    }
  };

  const handleChangeForm = (name, value) => {
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.close,
        variant: 'outlined',
        onClick: onClose
      },
      GridItemProps: {
        md: 3
      }
    },
    {
      props: {
        disabled: !canSave,
        isSaving: uiState.isSaving,
        initialText: strings.shared.ui.save,
        onClick: handleClickSave,
        onProgressText: strings.shared.ui.saving,
        variant: 'contained'
      },
      GridItemProps: {
        md: 3
      },
      mode: 'save'
    }
  ];

  return (
    <FPDrawer
      CardHeaderProps={{
        title: !isExistingSearch ? COPIES.titles.newSavedSearch : COPIES.titles.editSavedSearch
      }}
      FPDrawerProps={{
        open,
        onClose
      }}
      onClose={onClose}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
      hasPaddingContent={!(state.status === UIStatus.Loading)}
    >
      {!isExistingSearch ? (
        <Box display="flex" flexDirection="column" gridGap={16}>
          <TextBox
            autoFocus
            fullWidth
            label={COPIES.fields.name}
            name="name"
            noMargin
            onChange={handleChangeForm}
            value={formValues.name}
          />

          <TextBox
            fullWidth
            label={COPIES.fields.description}
            multiline
            name="description"
            noMargin
            onChange={handleChangeForm}
            value={formValues.description}
          />
        </Box>
      ) : (
        <StatusManager
          status={state?.status}
          slots={{
            Empty: <EmptyPlaceholder subtitle={COPIES.emptyState.title} />,
            Error: <EmptyPlaceholder title={state?.error} />,
            Loading: <LinearProgress />
          }}
        >
          <Box display="flex" flexDirection="column" gridGap={16}>
            <TextBox
              autoFocus
              fullWidth
              label={COPIES.fields.name}
              name="name"
              noMargin
              onChange={handleChangeForm}
              value={formValues.name}
            />

            <TextBox
              fullWidth
              label={COPIES.fields.description}
              multiline
              name="description"
              noMargin
              onChange={handleChangeForm}
              value={formValues.description}
            />
          </Box>
        </StatusManager>
      )}
    </FPDrawer>
  );
};
